/*
 * Curaprox PDP gallery — custom zoom cursor.
 *
 * Mirrors the shared `.cta--close` overlay button (Figma 2609:39847) — the same
 * primary-blue circle used to close every overlay, so the gallery's zoom and
 * close affordances match. On fine pointers, hovering the main gallery image
 * hides the native cursor and tracks the pointer with a circular follower
 * button. The follower is pointer-events:none so the click falls through to the
 * theme's existing zoom-dialog trigger on the slide.
 *
 * Touch devices (no hover) keep the default tap-to-open behaviour with no
 * follower, matching the mobile design.
 */

const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

// Zoomable media everywhere — the PDP carousel/grid AND the slides inside the
// open fullscreen dialog, where the follower doubles as the zoom/close cursor.
const ZOOM_SELECTOR = 'media-gallery .product-media-container--zoomable';

const MAGNIFIER_SVG = `
<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
  <circle cx="9" cy="9" r="5.25" stroke="currentColor" stroke-width="1.5"/>
  <path d="m13 13 3.25 3.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

// Inside the open dialog the glyph tracks what a click at the pointer would do
// (native-viewer ladder): on the photo it zooms in — magnifier-plus — until the
// third step, after which the next click zooms back out — magnifier-minus
// (Figma 2609:38564, "hover shows zoom out"; vector exported verbatim, the plus
// variant adds its vertical bar). On the letterbox/white area a click closes
// the modal (X).
const MAGNIFIER_IN_SVG = `
<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
  <path d="M15.5 15.5L11.875 11.875M4.66667 7.16667H9.66667M7.16667 4.66667V9.66667M13.8333 7.16667C13.8333 10.8486 10.8486 13.8333 7.16667 13.8333C3.48477 13.8333 0.5 10.8486 0.5 7.16667C0.5 3.48477 3.48477 0.5 7.16667 0.5C10.8486 0.5 13.8333 3.48477 13.8333 7.16667Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const MAGNIFIER_OUT_SVG = `
<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
  <path d="M15.5 15.5L11.875 11.875M4.66667 7.16667H9.66667M13.8333 7.16667C13.8333 10.8486 10.8486 13.8333 7.16667 13.8333C3.48477 13.8333 0.5 10.8486 0.5 7.16667C0.5 3.48477 3.48477 0.5 7.16667 0.5C10.8486 0.5 13.8333 3.48477 13.8333 7.16667Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const CLOSE_X_SVG = `
<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const GLYPHS = {
  'zoom-open': MAGNIFIER_SVG,
  'zoom-in': MAGNIFIER_IN_SVG,
  'zoom-out': MAGNIFIER_OUT_SVG,
  close: CLOSE_X_SVG,
};

class GalleryZoomCursor {
  /** @type {HTMLElement | null} */
  #follower = null;
  /** @type {Element | null} */
  #target = null;
  #active = false;
  /** @type {string | null} Which GLYPHS key the follower currently holds. */
  #glyph = null;
  #frame = 0;
  #x = 0;
  #y = 0;

  constructor() {
    if (!FINE_POINTER.matches) return;

    document.addEventListener('pointerover', this.#handlePointerOver, { passive: true });
    document.addEventListener('pointerout', this.#handlePointerOut, { passive: true });
    document.addEventListener('pointermove', this.#handlePointerMove, { passive: true });
    document.addEventListener('pointerdown', this.#handlePointerDown, { passive: true });
    // After a click INSIDE the dialog the zoom state changed (in ⇄ out), so
    // refresh the glyph in place — the pointer may not move.
    document.addEventListener('click', this.#handleClick, { passive: true });
    // Re-evaluate when the device gains/loses a fine pointer (e.g. tablet + mouse).
    FINE_POINTER.addEventListener?.('change', (event) => {
      if (!event.matches) this.#deactivate();
    });
  }

  /**
   * Returns the zoomable main-gallery container for a node, or null.
   * @param {EventTarget | null} node
   * @returns {Element | null}
   */
  #zoomTarget(node) {
    if (!(node instanceof Element)) return null;
    return node.closest(ZOOM_SELECTOR);
  }

  /**
   * Which glyph a click at (x, y) on this target would act out.
   * PDP gallery → plain magnifier (opens the modal). Inside the dialog, ask the
   * drag-zoom-wrapper whether the point lands on the photo (→ zoom in, or out
   * once the click ladder is exhausted) or the letterbox around it (→ close).
   * @param {Element} target
   * @param {number} x
   * @param {number} y
   * @returns {keyof typeof GLYPHS}
   */
  #glyphFor(target, x, y) {
    if (!target.closest('.dialog-zoomed-gallery')) return 'zoom-open';
    const wrapper = /** @type {any} */ (target.querySelector('drag-zoom-wrapper'));
    if (!wrapper?.hitsImage?.(x, y)) return 'close';
    return wrapper.nextClickZoomsOut ? 'zoom-out' : 'zoom-in';
  }

  /**
   * @param {keyof typeof GLYPHS} glyph
   * @param {Element} target - the hovered container, used to pick the host element
   */
  #ensureFollower(glyph, target) {
    let el = this.#follower;
    if (!el) {
      el = document.createElement('div');
      el.className = 'curaprox-zoom-cursor';
      el.setAttribute('aria-hidden', 'true');
      this.#follower = el;
    }

    // An open <dialog> paints in the top layer, which sits above ALL normal
    // content no matter the z-index — a body-level follower would be hidden
    // behind the zoom modal. Re-home it into the dialog while hovering there.
    // `position: fixed` stays viewport-relative either way, so the transform
    // maths is unaffected.
    const host = target.closest('dialog[open]') ?? document.body;
    if (el.parentElement !== host) host.appendChild(el);
    // Only rewrite when the glyph actually changes — pointermove would otherwise
    // re-parse the SVG on every frame.
    if (this.#glyph !== glyph) {
      this.#glyph = glyph;
      el.innerHTML = GLYPHS[glyph];
    }
    return el;
  }

  #handlePointerOver = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType !== 'mouse') return;
    const target = this.#zoomTarget(event.target);
    if (target) this.#activate(target, event);
  };

  /**
   * On the MAIN gallery a click opens the dialog, so hide the follower for that
   * hand-off. INSIDE the dialog a click only zooms (or closes on the letterbox),
   * so keep the follower alive — #handleClick then flips its glyph in place.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (this.#active && this.#target?.closest('.dialog-zoomed-gallery')) return;
    this.#deactivate();
  };

  /**
   * A click on the zoomed image changes the zoom state (in ⇄ out ⇄ fit), which
   * changes what the NEXT click does — so the follower glyph must update even if
   * the pointer never moves. The theme runs the actual zoom in a `document`
   * CAPTURE listener (see drag-zoom-wrapper.js), which fires before this
   * bubble-phase handler, so the wrapper's state is already up to date here.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#active || !this.#target) return;
    if (!this.#target.closest('.dialog-zoomed-gallery')) return;
    this.#ensureFollower(this.#glyphFor(this.#target, this.#x, this.#y), this.#target);
  };

  #handlePointerOut = (/** @type {PointerEvent} */ event) => {
    if (!this.#active) return;
    // Ignore moves between children that stay within the zoomable target.
    if (this.#zoomTarget(event.relatedTarget)) return;
    this.#deactivate();
  };

  #handlePointerMove = (/** @type {PointerEvent} */ event) => {
    if (event.pointerType !== 'mouse') return;
    if (!this.#active) {
      // Re-acquire when the pointer is already resting on a target with no
      // fresh pointerover (e.g. right after the dialog closes).
      const target = this.#zoomTarget(event.target);
      if (target) this.#activate(target, event);
      return;
    }
    this.#x = event.clientX;
    this.#y = event.clientY;
    // Inside the dialog the action (zoom in/out vs close) depends on WHERE the
    // pointer sits, so the glyph is re-evaluated per move (rewrites only on change).
    if (this.#target) this.#ensureFollower(this.#glyphFor(this.#target, this.#x, this.#y), this.#target);
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#render);
  };

  #render = () => {
    this.#frame = 0;
    if (!this.#follower) return;
    this.#follower.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0) translate(-50%, -50%)`;
  };

  /**
   * @param {Element} target
   * @param {PointerEvent} event
   */
  #activate(target, event) {
    this.#target = target;
    this.#active = true;
    this.#x = event.clientX;
    this.#y = event.clientY;
    target.classList.add('curaprox-zoom-cursor-active');
    const follower = this.#ensureFollower(this.#glyphFor(target, this.#x, this.#y), target);
    this.#render();
    follower.classList.add('is-visible');
  }

  #deactivate = () => {
    if (!this.#active && !this.#target) return;
    this.#active = false;
    if (this.#target) this.#target.classList.remove('curaprox-zoom-cursor-active');
    this.#target = null;
    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
    this.#follower?.classList.remove('is-visible');
  };
}

const init = () => {
  // The snippet may appear more than once if the whole section is re-rendered;
  // only ever wire up a single controller / follower.
  if (window.__curaproxZoomCursor) return;
  window.__curaproxZoomCursor = new GalleryZoomCursor();
};

if (document.readyState !== 'loading') {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
